'use client';

import { useParams } from 'next/navigation';
import { useTranslations } from 'use-intl';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CurrencyHelpers } from '@/utils/currency-helpers';
import { classnames } from '@/utils/classnames/classnames';
import { MIN_ORDER_THRESHOLD_CENTS } from '@/config/min-order';
import useProjectSettings from '@/lib/hooks/useProjectSettings';
import { MinOrderSurchargeBannerProps } from './types';

// EWM-224 — informational (non-blocking) warning shown on the cart + checkout when
// the items subtotal is below the global minimum order value. Per Luke (2026-07-07)
// the message is GENERIC: it states that a small-order surcharge will apply but NOT
// the amount — the freight service computes + applies that to the shipping cost.
// Self-gating: renders nothing at or above the threshold.
//
// Threshold source (precedence): explicit `thresholdCents` prop (tests/stories) →
// Studio project config via useProjectSettings (editable without a redeploy) →
// the MIN_ORDER_THRESHOLD_CENTS build-time fallback (used before projectSettings loads).
const MinOrderSurchargeBanner = ({ subtotal, thresholdCents, className }: MinOrderSurchargeBannerProps) => {
  const translate = useTranslations();
  const params = useParams();
  const locale = typeof params.locale === 'string' ? params.locale : 'en-AU';
  const { projectSettings } = useProjectSettings();

  const resolvedThresholdCents = thresholdCents ?? projectSettings?.minOrderThresholdCents ?? MIN_ORDER_THRESHOLD_CENTS;
  const subtotalCents = subtotal?.centAmount ?? 0;

  if (subtotalCents >= resolvedThresholdCents) return null;

  const currencyCode = subtotal?.currencyCode ?? 'AUD';
  const fractionDigits = subtotal?.fractionDigits ?? 2;
  const threshold = CurrencyHelpers.formatForCurrency(resolvedThresholdCents, locale, currencyCode, fractionDigits);

  return (
    <div
      role="alert"
      aria-live="polite"
      className={classnames(
        'flex items-start gap-3 rounded-lg border border-dashed border-red-400 bg-red-50 p-4',
        className,
      )}
    >
      <ExclamationTriangleIcon aria-hidden className="mt-0.5 size-5 shrink-0 text-red-600" />
      <div>
        <p className="text-14 font-bold text-red-700">{translate('cart.min-order-not-met')}</p>
        <p className="text-13 mt-1 text-gray-700">{translate('cart.min-order-surcharge-warning', { threshold })}</p>
      </div>
    </div>
  );
};

export default MinOrderSurchargeBanner;
